/* eslint-disable react/require-default-props */
import { KIND } from 'baseui/button';
import {
  FileUploader,
  FileUploaderProps,
  FileRow,
} from 'baseui/file-uploader';
import React, { useEffect, useRef, useState } from 'react';

import { styled } from 'theme';

export type FileInputProps = {
  defaultValue?: File[];
  onChange?: (value: File[]) => void;
  errorMessage?: string;
} & Omit<FileUploaderProps, 'fileRows' | 'setFileRows'>;

const StyledRoot = styled<any, { $hasError: boolean }>(
  'div',
  ({ $theme, $hasError }) => ({
    display: 'block',
    ...$theme.borders.border400,
    borderRadius: $theme.sizing.scale400,
    ...($hasError
      ? {
          backgroundColor: $theme.colors.negative50,
          borderColor: $theme.colors.negative,
        }
      : { backgroundColor: $theme.colors.fileUploaderBackgroundColor }),
  }),
);

const toFileRow = (file: File, index: number): FileRow => ({
  file,
  id: `${file.name}-${index}`,
  errorMessage: null,
  progressAmount: 100,
  status: 'processed',
});

const NO_FILES: File[] = [];

export default function FileInput({
  defaultValue = NO_FILES,
  onChange,
  errorMessage,
  ...BaseFileUploadProps
}: FileInputProps) {
  const [fileRows, setFileRows] = useState<FileRow[]>(() =>
    defaultValue.map(toFileRow),
  );
  const onChangeRef = useRef<typeof onChange | null>(null);
  const hasError = !!errorMessage;

  useEffect(() => {
    if (onChangeRef.current) {
      onChangeRef.current(fileRows.map(row => row.file));
    }
  }, [fileRows]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  return (
    <StyledRoot $hasError={hasError}>
      <FileUploader
        {...BaseFileUploadProps}
        fileRows={fileRows}
        setFileRows={rows =>
          setFileRows(
            rows.reduce((allRows, row) => {
              const isAdded = allRows.some(
                addedRow => addedRow.file.name === row.file.name,
              );
              return isAdded ? allRows : [...allRows, row];
            }, [] as FileRow[]),
          )
        }
        overrides={{
          ButtonComponent: {
            props: {
              kind: KIND.tertiary,
              overrides: {
                BaseButton: {
                  style: {
                    marginTop: 0,
                  },
                },
              },
            },
          },
          FileDragAndDrop: {
            style: {
              border: 0,
              backgroundColor: 'transparent',
            },
          },
        }}
      />
    </StyledRoot>
  );
}
